import { useState } from "react";
import styled from "styled-components";
import { Overlay } from "../../../styles/popup";

const Popup = styled.div`
  width: min(480px, calc(100vw - 48px));
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;

  > .title {
    color: #121212;
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
  }

  > img {
    width: 17px;
    height: 17px;
    cursor: pointer;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e8e8e8;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #666;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
  word-break: keep-all;

  > p {
    margin: 0;
    > .confirm-phrase {
      color: #d91818;
      font-weight: 700;
    }
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > label {
    color: #121212;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
  }

  > form {
    width: 100%;
  }

  > input,
  > form > input {
    width: 100%;
    height: 42px;
    padding: 0 12px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #121212;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
      color: #b7b7b7;
    }

    &:focus {
      border-color: #7f25fb;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px 20px;

  > button {
    min-width: 72px;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  > .cancel {
    background: #e8e8e8;
    color: #121212;
  }

  > .confirm {
    background: #d91818;
    color: #fff;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const CONFIRM_PHRASE = "종료를 진행합니다";

interface EndConfirmPopupProps {
  targetName: string;
  targetLabel: "미션" | "빙고";
  submitting?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

const EndConfirmPopup = ({
  targetName,
  targetLabel,
  submitting = false,
  onClose,
  onConfirm,
}: EndConfirmPopupProps) => {
  const [input, setInput] = useState("");
  const matched = input.trim() === CONFIRM_PHRASE;

  const handleConfirm = async () => {
    if (!matched || submitting) return;
    await onConfirm();
  };

  return (
    <Overlay $zIndex={1100}>
      <Popup>
        <Header>
          <div className="title">{targetLabel} 종료</div>
          <img
            src="/image-web/tournament/close.svg"
            alt="close"
            onClick={submitting ? undefined : onClose}
          />
        </Header>
        <Divider />
        <Body>
          <p>
            &apos;{targetName}&apos;{" "}
            {targetLabel === "빙고" ? "빙고를" : "미션을"} 종료합니다.
          </p>
          <p>
            종료 후 재시작은 절대 불가능합니다. 계속하려면 아래에{" "}
            <span className="confirm-phrase">{CONFIRM_PHRASE}</span> 를
            입력하세요.
          </p>
          <Field>
            <label htmlFor="end-confirm-input">확인 문구</label>
            <form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              <input
                id="end-confirm-input"
                name={`confirm-phrase-${Date.now()}`}
                type="text"
                value={input}
                disabled={submitting}
                placeholder={CONFIRM_PHRASE}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                readOnly
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                onChange={(e) => setInput(e.target.value)}
              />
            </form>
          </Field>
        </Body>
        <Divider />
        <Footer>
          <button
            type="button"
            className="cancel"
            disabled={submitting}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className="confirm"
            disabled={!matched || submitting}
            onClick={handleConfirm}
          >
            {submitting ? "종료 중..." : "종료"}
          </button>
        </Footer>
      </Popup>
    </Overlay>
  );
};

export default EndConfirmPopup;
